import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { PARTNER } from "@/constants/testIds";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast, toastApiError } from "@/components/altiora/toast";
import {
  PageShell, PageHeader, Card, Section, Table, Badge,
  PrimaryButton, GhostButton, DangerButton, Modal, EmptyState,
  SkeletonTable, IconButton, Breadcrumbs,
  NAVY, CHAMPAGNE, SLATE,
} from "@/components/altiora/PremiumUI";
import { CheckCircle2, XCircle, Building2, ShieldCheck, Search } from "lucide-react";

/**
 * PartnerDirectory · Founder / Admin approval console.
 *
 * Design-System-native (Phase B):
 *   · PageShell + TopBar + PageHeader (Landing-Grade)
 *   · Table primitive (accessible, animated, responsive scroll)
 *   · Confirmation Modal on suspend (destructive action guard)
 *   · Unified toasts via altioraToast
 *   · Search input (client-side filter — sub-100ms UX)
 *   · SkeletonTable on initial load
 */
export default function PartnerDirectory() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [query, setQuery]       = useState("");
  const [pendingSuspend, setPS] = useState(null);   // org to confirm-suspend
  const [busy, setBusy]         = useState(false);

  const isFounder = (user?.roles || []).some(r => r === "founder" || r === "admin");

  const T = (fr, en) => (lang === "fr" ? fr : en);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/directory");
      setOrgs(data.items || []);
    } catch (e) {
      toastApiError(e, T("Accès refusé", "Access denied"));
      nav("/app");
    } finally { setLoading(false); }
  }, [nav, lang]);

  useEffect(() => { if (isFounder) load(); }, [isFounder, load]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/partner/${id}/approve`);
      altioraToast.success(T("Organisme activé", "Organisation activated"));
      await load();
    } catch (e) { toastApiError(e); }
    finally { setBusy(false); }
  };

  const doSuspend = async () => {
    if (!pendingSuspend) return;
    setBusy(true);
    try {
      await api.post(`/partner/${pendingSuspend.org_id}/suspend`);
      altioraToast.success(T("Organisme suspendu", "Organisation suspended"));
      setPS(null);
      await load();
    } catch (e) { toastApiError(e); }
    finally { setBusy(false); }
  };

  if (!isFounder) return null;

  const filtered = orgs.filter(o => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (o.name || "").toLowerCase().includes(q)
        || (o.org_id || "").toLowerCase().includes(q)
        || (o.country || "").toLowerCase().includes(q);
  });

  const statusVariant = { active: "green", pending: "amber", suspended: "red" };

  const columns = [
    { key: "name",    label: T("Organisme", "Organisation"),
      render: (o) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold" style={{ color: NAVY }}>
            <Building2 size={13} aria-hidden="true" style={{ color: CHAMPAGNE }} />
            <span className="truncate">{o.name}</span>
          </div>
          <div className="text-[10px] font-mono mt-0.5 truncate" style={{ color: SLATE }}>
            {o.org_id}
          </div>
        </div>
      )},
    { key: "kind",    label: T("Type", "Kind"),
      render: (o) => <span className="text-[11px]" style={{ color: SLATE }}>{o.kind}</span> },
    { key: "country", label: T("Pays", "Country"),
      render: (o) => (
        <span className="text-[11px] font-mono" style={{ color: SLATE }}>
          {(o.country || "—").toUpperCase()} {o.region || ""}
        </span>
      )},
    { key: "status",  label: T("État", "Status"),
      render: (o) => <Badge variant={statusVariant[o.status] || "neutral"}>{o.status}</Badge> },
    { key: "actions", label: T("Actions", "Actions"),
      render: (o) => (
        <div className="flex gap-1.5 justify-end">
          {o.status !== "active" ? (
            <PrimaryButton onClick={() => approve(o.org_id)} disabled={busy}
                                testid={PARTNER.approveBtn}>
              <CheckCircle2 size={12} aria-hidden="true" />
              {T("Approuver", "Approve")}
            </PrimaryButton>
          ) : (
            <GhostButton onClick={() => setPS(o)} disabled={busy}>
              <XCircle size={12} aria-hidden="true" />
              {T("Suspendre", "Suspend")}
            </GhostButton>
          )}
        </div>
      )},
  ];

  return (
    <PageShell testid={PARTNER.directoryRoot}>
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <Breadcrumbs items={[
          { label: "ALTIORA", href: "/app" },
          { label: T("Fondateur", "Founder"), href: "/app/founder" },
          { label: T("Répertoire partenaires", "Partner directory") },
        ]} />
        <div className="mt-3" />
        <PageHeader
          eyebrow={T("ALTIORA · Gouvernance",     "ALTIORA · Governance")}
          title={T("Répertoire des partenaires",  "Partner directory")}
          subtitle={T(
            "Approuvez, activez ou suspendez les organisations autorisées à recevoir des dossiers via ALTIORA.",
            "Approve, activate or suspend organisations authorised to receive dossiers via ALTIORA."
          )}
          actions={
            <div className="flex items-center gap-2">
              <Badge variant="navy" icon={ShieldCheck}>Founder</Badge>
            </div>
          } />

        <Card className="p-5 mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={13} aria-hidden="true"
                          className="absolute left-3 top-1/2 -translate-y-1/2"
                          style={{ color: SLATE }} />
              <input
                aria-label={T("Rechercher un organisme", "Search organisation")}
                data-testid="partner-directory-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={T("Rechercher un organisme, un pays ou un identifiant…", "Search by name, country, id…")}
                className="w-full pl-9 pr-3 py-2 rounded-md text-[13px] outline-none transition"
                style={{ border: `1px solid ${CHAMPAGNE}55`, color: NAVY }}
              />
            </div>
            <div className="text-[11px] font-mono" style={{ color: SLATE }}>
              {filtered.length} / {orgs.length}
            </div>
          </div>
        </Card>

        <div className="mt-4">
          {loading ? (
            <SkeletonTable rows={5} cols={5} testid="partner-directory-skeleton" />
          ) : filtered.length === 0 ? (
            <Card className="p-4">
              <EmptyState icon={Building2}
                             title={query
                               ? T("Aucun résultat", "No result")
                               : T("Aucun organisme enregistré", "No organisation registered")}
                             description={query
                               ? T("Essayez d'ajuster votre recherche.", "Try adjusting your search.")
                               : T("Les organisations qui demandent l'accès au portail apparaîtront ici.",
                                   "Organisations requesting portal access will appear here.")}
                             testid="partner-directory-empty" />
            </Card>
          ) : (
            <Section>
              <Table testid="partner-directory-table"
                        caption={T("Liste des organisations partenaires", "Partner organisations list")}
                        columns={columns}
                        rows={filtered.map(o => ({ ...o, id: o.org_id }))} />
            </Section>
          )}
        </div>
      </div>

      {/* Suspend confirmation modal (guard destructive action) */}
      <Modal open={!!pendingSuspend} onClose={() => !busy && setPS(null)}
                 testid="partner-directory-suspend-modal"
                 size="sm"
                 title={T("Suspendre cet organisme ?", "Suspend this organisation?")}
                 description={pendingSuspend
                   ? T(`« ${pendingSuspend.name} » ne pourra plus recevoir de dossiers tant qu'il ne sera pas réactivé.`,
                       `"${pendingSuspend.name}" will no longer be able to receive dossiers until re-activated.`)
                   : ""}
                 footer={
                   <>
                     <GhostButton onClick={() => setPS(null)} disabled={busy}
                                       testid="partner-directory-suspend-cancel">
                       {T("Annuler", "Cancel")}
                     </GhostButton>
                     <DangerButton onClick={doSuspend} disabled={busy}
                                         testid="partner-directory-suspend-confirm">
                       {busy ? "…" : T("Suspendre", "Suspend")}
                     </DangerButton>
                   </>
                 } />
    </PageShell>
  );
}
